// Maps the reactive criteria onto the /web-api/listings/search querystring.
// Separate from criteriaContract (which owns the shareable page URL): the web
// API is leading here, so param names are lowercase and follow the contract in
// docs/superpowers/specs/web-api-contract.md.
//
// Location is sent as long/lat + radius, but only when coordinates are known
// (popular places and "nearby"); a typed place without coordinates filters
// nothing. bbox comes from the map viewport (useMapState) and wins over the
// radius server-side. Not sent yet: perPage.

import { BEDROOM_PLUS_FROM } from './criteriaContract';

const API_RANGE_PARAMS = {
  m2: ['m2min', 'm2max'],
  perceel: ['perceelmin', 'perceelmax']
};

// Maps the criteria key to the API param name; the two that are left happen to
// carry the same name in both contracts.
// `types` sends the filter name as-is; the API expands it to the raw housingKind
// values behind that filter (App\Request\HousingTypeFilter).
const CSV_PARAMS = { types: 'types', statuses: 'statuses' };

// The UI's top bedroom option means "or more"; send it as "N+", matching the
// label. Same convention as ListingSearchBar's summary.
const formatBeds = (beds) =>
  [...beds].sort((a, b) => a - b).map((bed) => (bed >= BEDROOM_PLUS_FROM ? `${BEDROOM_PLUS_FROM}+` : String(bed)));

export const toApiParams = (criteria, rangeBounds = {}, page = 1, bbox = null) => {
  const params = new URLSearchParams();

  if (page > 1) params.set('page', String(page));

  if (criteria.long !== null && criteria.lat !== null) {
    params.set('long', String(criteria.long));
    params.set('lat', String(criteria.lat));
    if (criteria.radius !== null) params.set('radius', String(criteria.radius));
  }

  // Rounded so the watched querystring stays stable across float noise
  if (bbox) params.set('bbox', bbox.map((value) => value.toFixed(6)).join(','));

  if (criteria.pmin) params.set('pmin', String(criteria.pmin));
  if (criteria.pmax) params.set('pmax', String(criteria.pmax));

  // A slider handle on its bound is "no restriction": omit that side, matching
  // the page URL contract in criteriaContract.
  Object.entries(API_RANGE_PARAMS).forEach(([key, [minParam, maxParam]]) => {
    const range = criteria[key];
    if (!range) return;
    const bounds = rangeBounds[key] || [0, Infinity];
    if (range[0] !== null && range[0] > bounds[0]) params.set(minParam, String(range[0]));
    if (range[1] !== null && range[1] < bounds[1]) params.set(maxParam, String(range[1]));
  });

  if (criteria.beds.length) params.set('beds', formatBeds(criteria.beds).join(','));

  Object.entries(CSV_PARAMS).forEach(([key, param]) => {
    if (criteria[key].length) params.set(param, criteria[key].join(','));
  });

  params.set('sort', criteria.sort);

  return params;
};
